import { faFileExcel, faFileWord, faFileImage, faFilePdf, faFile, faFileZipper } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import { DownloadCloud, Trash, UploadCloud } from "react-feather";
import { toast } from "react-toastify";
import { Button, Card, CardBody, CardFooter, CardHeader, Form } from "reactstrap";
import SweetAlert from "sweetalert2";
import { useAuth } from "../../context/AuthContext";
import useFetcher from "../../hooks/useFetcher";
import { useFiles } from "../../hooks/useFiles";
import useMutter from "../../hooks/useMutter";
import { fetchFiles, removeFile, downloadFile, generateZip } from "../../services/fileServices";
import { handleErrors } from "../../services/handleErrors";
import { BlurSpinner } from "../BlurSpinner";
import { ButtonSecondary } from "../../ui/buttons";

const Filemanager = ({ bodyStyle, onChange, entity, entityId, editable = true, hideHeader, fileNameStyle, fileIconStyle, disclaimer, reloadFiles, NP }) => {
  const { refreshToken } = useAuth();
  const [files, isLoadingFiles, , , refreshFiles] = useFetcher(fetchFiles, [{ entity, entityId }]);
  const [deleteFile, isLoadingDeleteFile] = useMutter(removeFile, [{ entity, entityId }]);
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const [isUploadingFiles, handleUploadFileButton, handleConfirmSelectedFiles] = useFiles();

  useEffect(() => {
    if (reloadFiles) {
      refreshFiles();
    }
  }, [reloadFiles]);

  const handleFilesChanges = () => {
    if (onChange) {
      onChange();
    }
    refreshFiles();
  };

  const getFileIconName = (mimetype) => {
    if (mimetype.includes("pdf")) {
      return faFilePdf;
    }
    if (mimetype.includes("msword") || mimetype.includes("officedocument.wordprocessingml.document") || mimetype.includes("opendocument.text")) {
      return faFileWord;
    }
    if (mimetype.includes("ms-excel") || mimetype.includes("officedocument.spreadsheetml.sheet") || mimetype.includes("opendocument.spreadsheet")) {
      return faFileExcel;
    }
    if (mimetype.includes("image")) {
      return faFileImage;
    }
    if (mimetype.includes("zip")) {
      return faFileZipper;
    }
    return faFile;
  };

  const handleRemoveFile = async (file) => {
    const { isConfirmed } = await SweetAlert.fire({
      title: `Borrar ${file.originalname}`,
      text: "¿Seguro que desea borrar el archivo?. Si continua no podrá recuperarlo.",
      icon: "warning",
      confirmButtonColor: "#bf0d3e",
      showCancelButton: true,
      showConfirmButton: true,
      dangerMode: true,
    });

    if (isConfirmed) {
      await deleteFile({ entity, entityId, fileId: file.id });
      handleFilesChanges();
      toast.success(`Archivo ${file.originalname} borrado exitosamente`);
    }
  };

  const handleDownloadFile = async (file) => {
    setIsDownloadingFile(true);
    try {
      await downloadFile({ entity, entityId, fileId: file.id });
      toast.success(`Archivo ${file.originalname} descargado exitosamente`);
    } catch (err) {
      handleErrors(err, toast.error, refreshToken);
    } finally {
      setIsDownloadingFile(false);
    }
  };

  const getFullNameByUser = (file) => {
    const { nombre, apellido } = file.usuario;
    return `${nombre} ${apellido}`;
  };

  const handleZipFiles = async () => {
    const filesId = files && files.map((file) => file.id);
    const fileName = `NotaDePedido${entityId}`;
    const { message } = await generateZip({ entity, entityId, filesId, fileName });
    toast.success(message);
    setTimeout(() => {
      refreshFiles();
    }, 2000);
  };

  return (
    <BlurSpinner isLoading={isLoadingFiles || isLoadingDeleteFile || isDownloadingFile || isUploadingFiles}>
      <Card className="h-100 mb-0">
        {hideHeader !== true && (
          <CardHeader className="pt-2 pb-2 d-flex justify-content-between align-items-center">
            <h6 className="m-1">Archivos:</h6>
            <Form className="p-absolute" style={{ height: "0px", width: "0px", overflow: "hidden" }}>
              <input
                id="upfile"
                type="file"
                onChange={() => handleConfirmSelectedFiles({ entity, entityId, handleFilesUploaded: handleFilesChanges })}
                multiple
                accept="image/jpeg,image/gif,image/png,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              />
            </Form>
            {editable ? (
              <Button outline size="xs" onClick={handleUploadFileButton}>
                <UploadCloud size="24" />
              </Button>
            ) : null}
          </CardHeader>
        )}
        <CardBody style={bodyStyle} className="file-body py-2 custom-scrollbar">
          {disclaimer && (
            <h6 className="h-100 d-flex justify-content-center align-content-center flex-wrap">
              * Le recordamos que no puede subir información referida a los asociados.
            </h6>
          )}
          {files && files.length > 0 ? (
            <div className="new-users activity">
              {files.map((file) => (
                <div className="media" key={file.id}>
                  <div className={NP ? "" : "gradient-round m-r-30 gradient-line-1 file-icon"} style={fileIconStyle}>
                    <FontAwesomeIcon icon={getFileIconName(file.mimetype)} size={NP ? "lg" : ""} />
                  </div>
                  <div className="media-body">
                    <h6 className="mb-1 f-w-700 text-break file-name" style={fileNameStyle}>
                      {file.originalname}
                    </h6>
                    <p className="file-creator">{file.usuario ? getFullNameByUser(file) : "No User"}</p>
                  </div>
                  <span className="pull-right file-actions">
                    {editable ? (
                      <Button size="xs" color="link secondary" onClick={() => handleRemoveFile(file)}>
                        <Trash size={NP ? "18" : "24"} color={NP ? "#f0292a" : "#bf0d3e"} />
                      </Button>
                    ) : null}
                    <Button size="xs" outline className="btn-pill" onClick={() => handleDownloadFile(file)}>
                      <DownloadCloud size={NP ? "18" : "24"} color={NP ? "#f0292a" : "#bf0d3e"} />
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-100 d-flex justify-content-center align-content-center flex-wrap">
              <h6>Aún no se han subido archivos</h6>
            </div>
          )}
        </CardBody>
        {hideHeader === true && editable && (
          <CardFooter className="p-1">
            <div className="d-flex justify-content-center align-items-center">
              <Form className="p-absolute" style={{ height: "0px", width: "0px", overflow: "hidden" }}>
                <input
                  id="upfile"
                  type="file"
                  onChange={() => handleConfirmSelectedFiles({ entity, entityId, handleFilesUploaded: handleFilesChanges })}
                  multiple
                  accept="image/jpeg,image/gif,image/png,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                />
              </Form>

              <ButtonSecondary style={{ fontSize: "12px" }} outline size="xs" className="d-flex align-items-center" onClick={handleUploadFileButton}>
                <span className="mr-2">
                  <UploadCloud size="18" />
                </span>
                Agregar Archivos
              </ButtonSecondary>
              {NP && files && files.length > 0 && editable && (
                <ButtonSecondary style={{ fontSize: "12px", margin: "0px" }} type="button" onClick={() => handleZipFiles()}>
                  Zip
                  <FontAwesomeIcon className="ml-2" icon="fa-regular fa-file-zipper" />
                </ButtonSecondary>
              )}
            </div>
          </CardFooter>
        )}
      </Card>
    </BlurSpinner>
  );
};

export default Filemanager;
